import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { toIsoCurrency } from '../../../../../../shared/utils/xtm-currency.utils';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
  Estimate,
  VendorOrder,
} from '../../../../../../shared/models/vendor-order.model';
import { JobDetails } from '../../../../../../shared/models/job-data.model';
import { VendorService } from '../../../../../../shared/services/vendor.service';
import {
  QuoteSowModalComponent,
  SowModalData,
} from '../quote-sow-modal/quote-sow-modal.component';
import { QuoteType } from '../quote-types';
import { DocumentRequirementChangesComponent } from '../../../../../../features/job-quotes/document-requirement-changes/document-requirement-changes.component';
import { PreviousQuotesComponent } from '../previous-quotes/previous-quotes.component';
import { DownloadLoadingDialogComponent } from '../../../../../../shared/components/download-loading-dialog/download-loading-dialog.component';
import { QuoteCostCardComponent } from '../quote-cost-card/quote-cost-card.component';
import { computeCostDeltas } from '../quote-cost-card/quote-cost.util';
import { CANCELLATION_REASON_REANALYSIS_APPROVAL_EXPIRED } from '../../../../../../shared/utils/cancellation-reason.utils';

@Component({
  selector: 'app-quote-progress',
  imports: [
    MatIcon,
    QuoteCostCardComponent,
    DocumentRequirementChangesComponent,
    PreviousQuotesComponent,
  ],
  templateUrl: './quote-progress.component.html',
  styleUrl: './quote-progress.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteProgressComponent {
  private readonly dialog = inject(MatDialog);
  private readonly vendorService = inject(VendorService);

  readonly order = input.required<VendorOrder>();
  readonly cancelledEstimates = input<Estimate[]>([]);

  private readonly docReqChanges = viewChild(
    DocumentRequirementChangesComponent,
  );

  readonly showPreviousQuotesTopBorder = computed(
    () => !this.docReqChanges()?.isVisible(),
  );

  readonly vendorEstimate = computed<Estimate | null>(() => {
    const o = this.order();
    const estimates = o.estimates ?? [];

    // Match by vendorId if available
    if (o.vendorId) {
      const match = estimates.find(
        (e) =>
          o.vendorId === e.vendorId ||
          o.vendorId === e.workflow_uid ||
          o.vendorId === e.estimationsId,
      );
      if (match) return match;
    }

    // Fall back to the estimate with an accepted quote
    const accepted = estimates.find(
      (e) => e.quote?.acceptedBy && e.quote?.acceptedDate,
    );
    if (accepted) return accepted;

    // Fall back to the single estimate if there's only one
    return estimates.length === 1 ? estimates[0] : null;
  });

  readonly vendorName = computed(
    () => this.vendorEstimate()?.name ?? 'Unknown Vendor',
  );

  readonly heading = computed(() =>
    this.vendorEstimate()?.quote?.reanalysis
      ? 'Updated final cost'
      : 'Selected quote',
  );

  readonly quote = computed(() => {
    const est = this.vendorEstimate();
    if (!est) return null;
    if (est.quote?.reanalysis) return est.quote.reanalysis;
    if (this.resolveRushedSelected(est)) {
      return est.quote?.rushed ?? est.quote?.normal ?? null;
    }
    return est.quote?.normal ?? est.quote?.rushed ?? null;
  });

  readonly deliveryType = computed<QuoteType>(() => {
    const est = this.vendorEstimate();
    if (!est) return 'Standard';
    if (this.resolveRushedSelected(est)) {
      return 'Rushed';
    }
    return est.quote?.rushed && !est.quote?.normal ? 'Rushed' : 'Standard';
  });

  readonly deliveryDate = computed(() => {
    const est = this.vendorEstimate();
    if (!est) return '--';
    const type = this.deliveryType();
    const raw =
      type === 'Rushed'
        ? est.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate
        : est.estimatedDeliveryDates?.normal?.estimatedDeliveryDate;

    if (raw) {
      return this.formatDate(raw);
    }

    if (est.deadline) {
      return this.formatDate(est.deadline);
    }

    return '--';
  });

  readonly quoteId = computed(() => this.order().jobNumber ?? '--');

  readonly currency = computed(() => {
    const est = this.vendorEstimate();
    if (est?.quote?.reanalysis) {
      return toIsoCurrency(est.quote.reanalysis.currency ?? est.currency);
    }
    return toIsoCurrency(est?.currency ?? this.quote()?.currency);
  });

  readonly costBreakdown = computed<{ concept: string; cost: number }[]>(() => {
    const q = this.quote();
    if (!q) return [];
    if (q.costs?.length) return q.costs;
    const items: { concept: string; cost: number }[] = [];
    if (q.dtpCost)
      items.push({ concept: 'Desktop publishing', cost: q.dtpCost });
    if (q.translationCost)
      items.push({ concept: 'File preparation', cost: q.translationCost });
    if (q.managementCost)
      items.push({ concept: 'Management', cost: q.managementCost });
    if (q.rushedFees)
      items.push({ concept: 'Rushed fees', cost: q.rushedFees });
    return items;
  });

  readonly costDeltas = computed<ReadonlyMap<string, number>>(() => {
    const est = this.vendorEstimate();
    const reanalysis = est?.quote?.reanalysis;
    if (!est || !reanalysis) return new Map();
    const base = this.resolveRushedSelected(est)
      ? (est.quote?.rushed ?? est.quote?.normal)
      : (est.quote?.normal ?? est.quote?.rushed);
    if (!base) return new Map();
    return computeCostDeltas(base, this.costBreakdown());
  });

  readonly totalCost = computed(() => {
    const q = this.quote();
    if (q?.totalCost != null) return q.totalCost;
    const est = this.vendorEstimate();
    if (est?.quote?.reanalysis) {
      return this.costBreakdown().reduce((total, item) => total + item.cost, 0);
    }
    return est?.priceMax ?? est?.priceMin ?? 0;
  });

  readonly recommendedPOTotal = computed<number | null>(() => {
    const order = this.order();
    if (order.estimates?.some((e) => !!e.quote?.reanalysis)) {
      return null;
    }
    if (
      order.jobStatus === 'CANCELLED' &&
      order.cancellationReason?.trim() ===
        CANCELLATION_REASON_REANALYSIS_APPROVAL_EXPIRED
    ) {
      return null;
    }

    return Math.ceil(this.totalCost() * 1.15 * 100) / 100;
  });

  readonly purchaseOrderId = computed(() => {
    const poId = this.order().purchaseOrderId;
    if (poId != null && poId !== 0 && String(poId).trim() !== '')
      return String(poId);
    return '--';
  });

  readonly vendorJobDetails = computed<JobDetails>(() => {
    const o = this.order();
    const est = this.vendorEstimate();
    const vName = est?.name ?? '';
    const vendorFiles = (o.files ?? []).filter((f) => f.vendorName === vName);
    const revision = (o.vendorRevisions ?? []).find(
      (vr) => vr.vendorName === vName,
    );

    return {
      files: vendorFiles,
      jobStatus: o.jobStatus ?? '',
      quote: est?.quote
        ? { acceptedBy: est.quote.acceptedBy, acceptedDate: undefined }
        : undefined,
      changeJustificationDtp: revision?.changeJustificationDtp,
    };
  });

  readonly hasAnyReclassification = computed(() => {
    const details = this.vendorJobDetails();
    return (details.files ?? []).some(
      (f) => f.isDtpFile !== f.isDtpFileRevised,
    );
  });

  readonly previousEstimates = computed<Estimate[]>(() => {
    const cancelled = this.cancelledEstimates();
    const source = cancelled.length
      ? cancelled
      : (this.order().estimates ?? []);
    const selected = this.vendorEstimate();
    if (!selected) return source;
    const selectedId =
      selected.vendorId ?? selected.workflow_uid ?? selected.estimationsId;
    return source.filter(
      (e) => (e.vendorId ?? e.workflow_uid ?? e.estimationsId) !== selectedId,
    );
  });

  readonly approvedPreviousEstimates = computed<Estimate[]>(() =>
    this.previousEstimates().filter((estimate) => {
      const status = estimate.jobStatus?.toUpperCase();
      if (status === 'REJECTED') return true;
      return this.isEstimateApproved(estimate);
    }),
  );

  readonly lowestCostId = computed<string | null>(() => {
    const estimates = this.approvedPreviousEstimates().filter(
      (estimate) => estimate.jobStatus?.toUpperCase() !== 'REJECTED',
    );

    // The selected vendor's displayed alternative is its standard quote when
    // rushed was chosen, so it also competes for the "Lowest Standard Quote" badge.
    const selected = this.vendorEstimate();
    if (
      selected &&
      this.resolveRushedSelected(selected) &&
      selected.quote?.normal
    ) {
      estimates.push(selected);
    }

    if (!estimates.length) return null;
    let lowestCost = Infinity;
    let lowestId: string | null = null;
    for (const est of estimates) {
      const cost = est.quote?.normal?.totalCost ?? est.priceMax ?? est.priceMin;
      if (cost == null) continue;
      const id = est.vendorId ?? est.estimationsId ?? null;
      if (id == null) continue;
      if (cost < lowestCost) {
        lowestCost = cost;
        lowestId = id;
      }
    }
    return lowestId;
  });

  async generateQuotePDF(): Promise<void> {
    const loadingRef = this.dialog.open(DownloadLoadingDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });

    const order = this.order();
    const estimate = this.vendorEstimate();
    if (!estimate) {
      loadingRef.close();
      return;
    }

    let breakdown;
    try {
      const targetLocale = Array.isArray(order.targetLocaleId)
        ? order.targetLocaleId.join(',')
        : (order.targetLocaleId ?? '');
      breakdown =
        order.jobCreatedIn === 'xtm'
          ? await this.vendorService.getXtmCostBreakdown(
              order.jobUid ?? '',
              estimate.costId ?? '',
              estimate.costBreakdownId ?? '',
            )
          : await this.vendorService.getEstimateReportBreakdown(
              order.contentType ?? '',
              order.sourceLocaleId ?? '',
              targetLocale,
              estimate.estimationsId ?? '',
            );
    } catch {
      breakdown = undefined;
    }

    let vendor;
    try {
      vendor = estimate.vendorId
        ? await this.vendorService.getVendorDetails(estimate.vendorId)
        : undefined;
    } catch {
      vendor = undefined;
    }

    const modalData: SowModalData = {
      order,
      estimate,
      selectedQuoteType: this.deliveryType(),
      breakdown,
      vendor,
    };

    const dialogRef = this.dialog.open(QuoteSowModalComponent, {
      data: modalData,
      width: '960px',
      maxWidth: '95vw',
      height: '65vh',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });

    await firstValueFrom(dialogRef.afterOpened());
    await new Promise((resolve) => setTimeout(resolve, 300));
    loadingRef.close();
  }

  async openSowModal(): Promise<void> {
    const order = this.order();
    const estimate = this.vendorEstimate();
    if (!estimate) return;

    let breakdown;
    try {
      const targetLocale = Array.isArray(order.targetLocaleId)
        ? order.targetLocaleId.join(',')
        : (order.targetLocaleId ?? '');
      breakdown =
        order.jobCreatedIn === 'xtm'
          ? await this.vendorService.getXtmCostBreakdown(
              order.jobUid ?? '',
              estimate.costId ?? '',
              estimate.costBreakdownId ?? '',
            )
          : await this.vendorService.getEstimateReportBreakdown(
              order.contentType ?? '',
              order.sourceLocaleId ?? '',
              targetLocale,
              estimate.estimationsId ?? '',
            );
    } catch {
      breakdown = undefined;
    }

    let vendor;
    try {
      vendor = estimate.vendorId
        ? await this.vendorService.getVendorDetails(estimate.vendorId)
        : undefined;
    } catch {
      vendor = undefined;
    }

    const modalData: SowModalData = {
      order,
      estimate,
      selectedQuoteType: this.deliveryType(),
      breakdown,
      vendor,
    };

    this.dialog.open(QuoteSowModalComponent, {
      data: modalData,
      width: '960px',
      maxWidth: '95vw',
      height: '95vh',
      panelClass: 'no-padding-dialog',
      disableClose: false,
    });
  }

  private formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '--';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }

  /** Returns true when the rushed delivery type was selected for this estimate. */
  private resolveRushedSelected(est: Estimate): boolean {
    return est.rushedDateSelected === true;
  }

  private isEstimateApproved(estimate: Estimate): boolean {
    const quote = estimate.quote as
      | {
          acceptedBy?: string;
          acceptedDate?: string;
          normal?: { acceptedBy?: string; acceptedDate?: string };
          rushed?: { acceptedBy?: string; acceptedDate?: string };
        }
      | undefined;

    const acceptedBy =
      quote?.acceptedBy?.trim() ??
      quote?.normal?.acceptedBy?.trim() ??
      quote?.rushed?.acceptedBy?.trim();
    const acceptedDate =
      quote?.acceptedDate?.trim() ??
      quote?.normal?.acceptedDate?.trim() ??
      quote?.rushed?.acceptedDate?.trim();

    return !!acceptedBy && !!acceptedDate;
  }
}
